/// <reference types="@angular/localize" />
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from "@sentry/angular-ivy";
import { bootstrapApplication } from '@angular/platform-browser';
// posthog-js is dynamically imported after bootstrap (see analytics.service.ts's
// preloadAnalytics()) so its ~305KB stays out of the initial bundle and never
// blocks first paint.
// hammerjs was removed: HammerModule was never registered and nothing calls
// the global Hammer() API directly, so it was pure dead weight in the bundle.

import { AppModule } from './app/app.module';
import { environment } from './environments/environment.prod';
import { enableProdMode } from '@angular/core';
import { preloadAnalytics, onAnalyticsReady, captureSentryLink } from './app/core/services/analytics.service';
import { SafeStorage } from './app/core/utils/safe-storage';
import { stripAppRedirectToken } from './app/core/utils/app-redirect-token';

// Before Sentry, Angular or any analytics can read the URL: the app-redirect
// login token must never reach a session replay or a pageview.
stripAppRedirectToken();

if (environment.production) {
  enableProdMode();
  // Suppress verbose console output in production — set localStorage.debug='true' in browser console to re-enable.
  // Uses SafeStorage: this runs before Angular bootstraps, so a blocked/unavailable
  // localStorage here (private browsing, in-app browsers) would otherwise crash the
  // app before any error handling exists to catch it.
  if (SafeStorage.getItem('debug') !== 'true') {
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
  }
}

// Detect iOS Safari
const isIOSSafari = /iPhone|iPad|iPod/.test(navigator.userAgent) &&
  /Safari/.test(navigator.userAgent) &&
  !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

// Register service worker after app loads, with iOS Safari protection
if ("serviceWorker" in navigator && !isIOSSafari) {
  // Delay registration to avoid blocking initial load and Angular bootstrap
  window.addEventListener('load', () => {
    setTimeout(() => {
      navigator.serviceWorker.register("/custom-sw.js")
        .then((reg) => {
          // console.log("✅ Custom SW registered"); //__CONSOLE_LOG__

          navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data) {
              // if (event.data.type === "NEW_VERSION") {
              //   if (confirm("A new version is available. Reload now?")) {
              //     window.location.reload();
              //   }
              // }

              if (event.data.type === "API_UPDATED") {
                // console.log("✅ API updated:", event.data.url); //__CONSOLE_LOG__
                document.dispatchEvent(new CustomEvent("api-updated", { detail: event.data.url }));
              }
            }
          });
        })
        .catch((err) => {
          // console.warn("Service worker registration failed:", err); //__CONSOLE_LOG__
        });
    }, 1000); // Delay 1 second after load to ensure app is fully bootstrapped
  });
} else if (isIOSSafari) {
  // console.log("iOS Safari detected - service worker disabled to prevent crashes"); //__CONSOLE_LOG__
}

// Suppress unhandled Promise rejections from third-party SDKs and transient network errors
// that Zone.js surfaces loudly but are not actionable.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg: string = reason?.message ?? String(reason ?? '');
  const stack: string = reason?.stack ?? '';

  // Clarity SDK internal null-ref bug
  if (stack.includes('clarity.ms') || stack.includes('clarity.js')) {
    event.preventDefault();
    return;
  }

  // Transient network failures (offline, DNS, SSL) — Firebase & our own API calls
  // "Failed to fetch" / "Load failed" / "NetworkError" are all browser-level errors,
  // not bugs in our code. They appear when the user's connection drops momentarily.
  //
  // auth/popup-closed-by-user, auth/popup-blocked, auth/cancelled-popup-request are all
  // the user dismissing/blocking the sign-in popup themselves, not app bugs.
  if (
    msg.includes('Failed to fetch') ||
    msg.includes('Load failed') ||
    msg.includes('NetworkError') ||
    msg.includes('Network request failed') ||
    msg.includes('network-request-failed') ||
    msg.includes('Unable to refresh token') ||
    msg.includes('Unknown file format') ||  // exifr throws this for videos/PNGs with no EXIF
    msg.includes('auth/popup-closed-by-user') ||
    msg.includes('auth/popup-blocked') ||
    msg.includes('auth/cancelled-popup-request')
  ) {
    event.preventDefault();
  }
});

// Used to build links from PostHog back into Sentry. Neither value can be
// derived from the DSN: it carries the numeric org id (o4511327758057472) and
// project id, but not the org slug, which is what the Sentry UI's URLs use.
const SENTRY_ORG_SLUG = 'proximous';
const SENTRY_PROJECT_ID = '4511333252988928';

Sentry.init({
  dsn: "https://02ea3974eb767577bcc7ed022b472cef@o4511327758057472.ingest.us.sentry.io/4511333252988928",
  // Disable Sentry's console instrumentation so browser logs show original caller (no console.js:36)
  integrations: (defaults) => [
    // Keep all defaults but replace Breadcrumbs to turn off console capturing
    ...defaults.map((i: any) => i.name === 'Breadcrumbs' ? new (Sentry as any).Breadcrumbs({ console: false }) : i),
    // Explicitly add BrowserTracing. Session replay is handled by PostHog in prod,
    // so we drop @sentry/replay to keep it out of the initial bundle (~287KB).
    new (Sentry as any).BrowserTracing({
      routingInstrumentation: (Sentry as any).routingInstrumentation,
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.4,

  // Drop transient network errors — not actionable, just noise from momentary connectivity drops.
  // Also drop the Firebase popup codes that just mean the user closed/blocked/re-triggered
  // the sign-in popup themselves — expected user behavior, not a bug.
  beforeSend(event) {
    const msg: string = event.exception?.values?.[0]?.value ?? '';
    if (
      msg.includes('Failed to fetch') ||
      msg.includes('Load failed') ||
      msg.includes('NetworkError') ||
      msg.includes('Network request failed') ||
      msg.includes('network-request-failed') ||
      msg.includes('Unable to refresh token') ||
      msg.includes('Unknown file format') ||
      // Injected by the Facebook / Instagram in-app browsers, not by our code.
      msg.includes('XMLHttpRequest.prototype.open is overridden') ||
      msg.includes('auth/popup-closed-by-user') ||
      msg.includes('auth/popup-blocked') ||
      msg.includes('auth/cancelled-popup-request')
    ) {
      return null; // Drop from Sentry entirely
    }

    // Mirror the error into PostHog with a link back to the Sentry issue, so a
    // drop-off in the funnel can be asked "did this person hit an error?".
    // Only the linkage and a message — Sentry keeps the stack trace.
    try {
      captureSentryLink({
        $exception_message: msg || event.message || 'unknown',
        $exception_type: event.exception?.values?.[0]?.type ?? undefined,
        $sentry_event_id: event.event_id,
        $sentry_url: event.event_id
          ? `https://${SENTRY_ORG_SLUG}.sentry.io/issues/?project=${SENTRY_PROJECT_ID}&query=${event.event_id}`
          : undefined,
      });
    } catch { /* never let analytics break error reporting */ }

    return event;
  },

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/events.picaggo.com/, /^https:\/\/test-picaggo.web.app/],
});


/** Defer analytics until the browser is idle so it never blocks first paint. */
function scheduleDeferred(fn: () => void): void {
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn, { timeout: 4000 });
  } else {
    setTimeout(fn, 2000);
  }
}

platformBrowserDynamic().bootstrapModule(AppModule)
  .then(() => scheduleDeferred(() => {
    preloadAnalytics();
    // The other direction: tag Sentry issues with who this is in PostHog, so an
    // error leads to the person's session rather than an anonymous stack trace.
    onAnalyticsReady(ph => {
      try {
        Sentry.setTag('posthog_distinct_id', ph.get_distinct_id());
        const replayUrl = (ph as any).get_session_replay_url?.({ withTimestamp: true });
        if (replayUrl) {
          Sentry.setTag('posthog_session_url', replayUrl);
        }
      } catch { /* linking is best-effort; never break error reporting */ }
    });
  }))
  .catch(err => console.error(err));
